import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Card, CardContent } from '@components/ui/card';
import { UserContext } from '..';
import { ActivityDto } from '../../../core/api/FitnessActivityClient';
import { getColorForId } from '../flags-tool/utils/GpxMap.utils';
import { useWorkoutList } from './utils/useWorkoutList';
import { WorkoutPicker } from './components/WorkoutPicker';
import { TrackLoader } from './components/TrackLoader';
import { TrackMap } from './components/TrackMap';
import { TimelineScrubber } from './components/TimelineScrubber';
import { MetricChart } from './components/MetricChart';
import { PointDetailsPanel } from './components/PointDetailsPanel';
import { EnrichedTrack, SelectedWorkout } from './types';

const MAX_SELECTED = 5;

const formatLabel = (w: ActivityDto): string => {
    const from = w.dateTimeRange.from;
    const stamp = `${from.slice(0, 10)} ${from.slice(11, 16)}`;
    const parts = [`#${w.id}`, stamp];
    if (w.workoutTypeV2) parts.push(w.workoutTypeV2);
    return parts.join(' · ');
};

const GpxExplorer = observer(() => {
    const user = React.useContext(UserContext);
    const userId = user.id.toString();

    const { workouts, isLoading, error, hasMore, loadMore } =
        useWorkoutList(userId);

    const [selectedWorkouts, setSelectedWorkouts] = useState<SelectedWorkout[]>(
        []
    );
    const [tracksMap, setTracksMap] = useState<Map<number, EnrichedTrack>>(
        new Map()
    );
    const [progress, setProgress] = useState(0);
    const [focusedTrackId, setFocusedTrackId] = useState<number | null>(null);

    const handleToggleWorkout = useCallback((w: ActivityDto) => {
        setSelectedWorkouts((prev) => {
            const existing = prev.find((s) => s.id === w.id);
            if (existing) {
                return prev.filter((s) => s.id !== w.id);
            }
            if (prev.length >= MAX_SELECTED) return prev;
            return [
                ...prev,
                {
                    id: w.id,
                    color: getColorForId(w.id.toString()),
                    label: formatLabel(w)
                }
            ];
        });
    }, []);

    const handleRemoveSelection = useCallback((id: number) => {
        setSelectedWorkouts((prev) => prev.filter((s) => s.id !== id));
    }, []);

    const handleTrackChange = useCallback(
        (id: number, track: EnrichedTrack | null) => {
            setTracksMap((prev) => {
                const next = new Map(prev);
                if (track) {
                    next.set(id, track);
                } else {
                    next.delete(id);
                }
                return next;
            });
        },
        []
    );

    React.useEffect(() => {
        if (focusedTrackId !== null && !tracksMap.has(focusedTrackId)) {
            setFocusedTrackId(null);
        }
        if (focusedTrackId === null && tracksMap.size > 0) {
            const first = selectedWorkouts.find((s) => tracksMap.has(s.id));
            if (first) setFocusedTrackId(first.id);
        }
    }, [tracksMap, focusedTrackId, selectedWorkouts]);

    const loadedTracks = useMemo<EnrichedTrack[]>(
        () =>
            selectedWorkouts
                .map((sw) => tracksMap.get(sw.id))
                .filter((t): t is EnrichedTrack => Boolean(t)),
        [selectedWorkouts, tracksMap]
    );

    const focusedTrack =
        focusedTrackId !== null
            ? (tracksMap.get(focusedTrackId) ?? null)
            : null;
    const focusedWorkout =
        focusedTrackId !== null
            ? (selectedWorkouts.find((s) => s.id === focusedTrackId) ?? null)
            : null;

    const handleMapClick = useCallback(
        (workoutId: number, pointIdx: number) => {
            const track = tracksMap.get(workoutId);
            if (!track) return;
            const n = track.points.length;
            if (n <= 1) return;
            setFocusedTrackId(workoutId);
            setProgress(pointIdx / (n - 1));
        },
        [tracksMap]
    );

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="mb-1 text-lg font-semibold tracking-tight">
                    GPX Explorer
                </h2>
                <p className="text-xs text-muted-foreground">
                    Deep point-by-point analysis. Pick up to {MAX_SELECTED}{' '}
                    workouts to overlay. Use ◀/▶ (Shift × 10) or click the map
                    / charts to scrub.
                </p>
            </div>

            <Card>
                <CardContent className="flex flex-col gap-4">
                    <WorkoutPicker
                        workouts={workouts}
                        isLoading={isLoading}
                        error={error}
                        hasMore={hasMore}
                        onLoadMore={loadMore}
                        selected={selectedWorkouts}
                        maxSelected={MAX_SELECTED}
                        onToggle={handleToggleWorkout}
                    />
                    {selectedWorkouts.length > 0 && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                            {selectedWorkouts.map((sw) => (
                                <TrackLoader
                                    key={sw.id}
                                    workout={sw}
                                    isFocused={focusedTrackId === sw.id}
                                    onFocus={() => setFocusedTrackId(sw.id)}
                                    onRemove={() =>
                                        handleRemoveSelection(sw.id)
                                    }
                                    onTrackChange={handleTrackChange}
                                />
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {selectedWorkouts.length === 0 ? (
                <Alert variant="info">
                    <AlertDescription>
                        Select one or more workouts above to begin analysis.
                    </AlertDescription>
                </Alert>
            ) : (
                <>
                    <TimelineScrubber
                        progress={progress}
                        onProgressChange={setProgress}
                        focusedTrack={focusedTrack}
                    />

                    <div className="grid gap-4 lg:grid-cols-3">
                        <div className="flex flex-col gap-4 lg:col-span-2">
                            {loadedTracks.length > 0 ? (
                                <TrackMap
                                    tracks={loadedTracks}
                                    selectedWorkouts={selectedWorkouts}
                                    progress={progress}
                                    focusedTrackId={focusedTrackId}
                                    onMapClick={handleMapClick}
                                />
                            ) : (
                                <Alert variant="info">
                                    <AlertDescription>
                                        Waiting for track data…
                                    </AlertDescription>
                                </Alert>
                            )}
                            <MetricChart
                                kind="speed"
                                title="Speed"
                                yLabel="km/h"
                                tracks={loadedTracks}
                                selectedWorkouts={selectedWorkouts}
                                progress={progress}
                                onProgressChange={setProgress}
                            />
                            <MetricChart
                                kind="acceleration"
                                title="Acceleration"
                                yLabel="m/s²"
                                tracks={loadedTracks}
                                selectedWorkouts={selectedWorkouts}
                                progress={progress}
                                onProgressChange={setProgress}
                            />
                            <MetricChart
                                kind="elevation"
                                title="Elevation"
                                yLabel="m"
                                tracks={loadedTracks}
                                selectedWorkouts={selectedWorkouts}
                                progress={progress}
                                onProgressChange={setProgress}
                            />
                        </div>
                        <PointDetailsPanel
                            track={focusedTrack}
                            workout={focusedWorkout}
                            progress={progress}
                        />
                    </div>
                </>
            )}
        </div>
    );
});

export default GpxExplorer;
